import type { PasoSolicitud, StepDef } from "./steps-config";

/**
 * Helpers puros del stepper · sin React, sin Mantine. Mapean el estado del
 * backend al paso semántico y luego a su índice dentro de la lista de pasos
 * del tipo de solicitud (FB60 tiene 8 pasos; Contrato Anualizado inserta
 * "Contrato anualizado" y tiene 9). Trabajar por CLAVE de paso (no por índice fijo)
 * mantiene el mapeo correcto para ambos tipos.
 */

/**
 * Paso semántico activo según el estado del backend + el tipo/subtipo de
 * solicitud. P44 · FB60 Contrapago NO introduce estados nuevos en el backend
 * (el CFDI llega después del pago, pero el resto del ciclo de vida reutiliza
 * los mismos valores de `Estado` que cualquier FB60 · "en espera de CFDI" es
 * paralelo, ver `esperaCfdiConciliadoAt`) · solo dos puntos divergen:
 * `SubtipoSeleccionado` (va a "organizacion" en vez de "cfdi", no hay CFDI que
 * cargar) y `Autorizada` (se queda en "conciliacion" hasta que el CFDI real
 * se concilie, en vez de ser terminal de inmediato).
 */
export function pasoActivo(
  estado: string | null,
  tipo: string | null | undefined,
  subtipo?: string | null,
  esperaCfdiConciliadoAt?: string | null
): PasoSolicitud | "terminal-positivo" {
  if (estado === null) return "crear";
  const esContrapago = subtipo === "CONTRAPAGO";

  switch (estado) {
    // Crear (tipo + subtipo)
    case "Iniciada":
    case "TipoSeleccionado":
      return "crear";

    // SubtipoSeleccionado: para CA el siguiente paso es el contrato anualizado,
    // para OC la orden de compra, para Contrapago la organización manual
    // (no hay CFDI del que derivarla); para FB60 normal es directamente el CFDI.
    case "SubtipoSeleccionado":
      if (esContrapago) return "organizacion";
      if (tipo === "ContratoAnualizado") return "contrato";
      if (tipo === "OrdenDeCompra") return "orden";
      return "cfdi";

    // Contrato anualizado ya seleccionado (CA) · toca cargar el CFDI.
    case "ContratoAnualizadoSeleccionado":
      return "cfdi";

    // Orden de compra ya seleccionada (OC) · toca cargar el CFDI.
    case "OrdenDeCompraSeleccionada":
      return "cfdi";

    // CFDI / ZIP
    case "CfdiPendiente":
    case "CfdiCargado":
    case "EnValidacionReglas":
    case "ReglasRechazadas":
    case "EnValidacionFiscal":
    case "RechazadoFiscal":
    case "SuspendidaFiscal":
      return "cfdi";

    // Datos generales
    case "Validado":
    case "CapturandoDatos":
      return "datos";

    // Proveedor
    case "SeleccionandoProveedor":
      return "proveedor";

    // Prorrateo
    case "Prorrateando":
      return "prorrateo";

    // Documentos
    case "ProvisionalmenteListo":
    case "AdjuntandoDocumentos":
    case "RechazadaParaCorreccion":
      return "documentos";

    // Autorización
    case "ListoParaAutorizacion":
    case "EnAutorizacionNivel1":
    case "EnAutorizacionNivelN":
      return "autorizacion";

    // Autorizada: Contrapago se queda "en espera de CFDI" (paso conciliación)
    // hasta que el CFDI real llegue y pase la conciliación · el resto de
    // subtipos ya está completo en cuanto se autoriza.
    case "Autorizada":
      if (esContrapago) return esperaCfdiConciliadoAt ? "terminal-positivo" : "conciliacion";
      return "terminal-positivo";

    // Terminales positivos · todo el flujo completo
    case "EnviadaAContabilizacion":
      return "terminal-positivo";

    default:
      return "crear";
  }
}

/**
 * Índice del step activo dentro de `steps`. Devuelve `steps.length` para los
 * estados terminales positivos (marca TODOS los pasos como completados).
 */
export function mapearEstadoAStep(
  estado: string | null,
  tipo: string | null | undefined,
  steps: ReadonlyArray<StepDef>,
  subtipo?: string | null,
  esperaCfdiConciliadoAt?: string | null
): number {
  const paso = pasoActivo(estado, tipo, subtipo, esperaCfdiConciliadoAt);
  if (paso === "terminal-positivo") return steps.length;
  const idx = steps.findIndex((s) => s.key === paso);
  return idx < 0 ? 0 : idx;
}

export function indexDePaso(
  paso: PasoSolicitud,
  steps: ReadonlyArray<StepDef>
): number {
  const idx = steps.findIndex((s) => s.key === paso);
  return idx < 0 ? 0 : idx;
}

/**
 * URL de la step page de cada paso semántico. CFDI y Prorrateo varían por
 * subtipo. "Crear" (tipo+subtipo) retorna null porque su info ya está en el
 * header de cada página · no hay pantalla dedicada a la que saltar.
 */
export function urlPorPaso(
  paso: PasoSolicitud | undefined,
  solicitudId: string,
  esMultiple: boolean,
  esContrapago = false,
  esInvoice = false
): string | null {
  switch (paso) {
    case "crear":
      return null;
    case "contrato":
      return `/solicitudes/${solicitudId}/contrato-anualizado`;
    case "orden":
      return `/solicitudes/${solicitudId}/orden-compra`;
    case "organizacion":
      return `/solicitudes/${solicitudId}/organizacion-manual`;
    case "cfdi":
      return esInvoice
        ? `/solicitudes/${solicitudId}/carga-invoice`
        : esMultiple
          ? `/solicitudes/${solicitudId}/cargar-zip`
          : `/solicitudes/${solicitudId}/cargar-cfdi`;
    case "datos":
      return `/solicitudes/${solicitudId}/datos-generales`;
    case "proveedor":
      // Invoice y Contrapago seleccionan el proveedor a mano (emisor extranjero / sin CFDI aún).
      return esContrapago || esInvoice
        ? `/solicitudes/${solicitudId}/proveedor-manual`
        : `/solicitudes/${solicitudId}/proveedor`;
    case "prorrateo":
      // Invoice y Contrapago cuadran el prorrateo contra un total (sin subtotal de CFDI del SAT).
      return esContrapago || esInvoice
        ? `/solicitudes/${solicitudId}/prorrateo-sin-cfdi`
        : esMultiple
          ? `/solicitudes/${solicitudId}/prorrateo-multiple`
          : `/solicitudes/${solicitudId}/prorrateo`;
    case "documentos":
      return `/solicitudes/${solicitudId}/documentos`;
    case "contabilizacion":
      return `/solicitudes/${solicitudId}/contabilizacion`;
    case "autorizacion":
      return `/solicitudes/${solicitudId}/autorizacion`;
    case "conciliacion":
      return `/solicitudes/${solicitudId}/conciliar-cfdi`;
    default:
      return null;
  }
}

/**
 * Re-escribe la descripción del step "Crear" según el tipo + subtipo elegidos.
 */
export function descripcionCrear(
  tipo: string | null | undefined,
  subtipo: string | null
): string {
  if (tipo === "ContratoAnualizado") {
    return subtipo === "INVOICE"
      ? "Contrato Anualizado · Invoice"
      : subtipo === "MULTIPLES_CFDI"
        ? "Contrato Anualizado · Múltiples CFDI"
        : "Contrato Anualizado · CFDI único";
  }
  if (tipo === "OrdenDeCompra") {
    return subtipo === "INVOICE" ? "Orden de Compra · Invoice" : "Orden de Compra · CFDI único";
  }
  switch (subtipo) {
    case "CFDI_UNICO":
      return "FB60 · CFDI único";
    case "MULTIPLES_CFDI":
      return "FB60 · Múltiples CFDI";
    case "CONTRAPAGO":
      return "FB60 · Contrapago";
    default:
      return "Tipo y subtipo";
  }
}
